
import { useState, useEffect } from "react";
import { Sparkles, Plus, RefreshCw, X, BarChart2, ShieldCheck, Bot } from "lucide-react";
import { usersListUsers } from "../../gen";
import type { UserResponse } from "../../gen/models";
import {
  normalizeAgentType,
  sanitizeProtocoloName,
} from "../../hooks/useUserDirectory";

interface NewConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedAgent: string;
  onSelectAgent: (agentId: string) => void;
  onSubmit: () => void;
  creating: boolean;
}

interface AgentDisplayItem {
  id: string;
  name: string;
  badge: string;
  desc: string;
  icon: any;
  color: string;
}

const AGENT_DEFAULTS: Record<
  string,
  {
    name: string;
    badge: string;
    desc: string;
    icon: any;
    color: string;
  }
> = {
  biblioteca: {
    name: "Analytics",
    badge: "Analytics Lead",
    desc: "Explore CRM leads, marketing metrics, and revenue analytics via natural language SQL queries.",
    icon: BarChart2,
    color: "text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/60 border-indigo-200 dark:border-indigo-800",
  },
  sales: {
    name: "Quotes",
    badge: "Sales Concierge",
    desc: "Check live rates and availability, craft bespoke stay proposals, and send quotes.",
    icon: Sparkles,
    color: "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 border-emerald-200 dark:border-emerald-800",
  },
  frontdesk: {
    name: "Sofia",
    badge: "Front Desk Concierge",
    desc: "Simulate guest concierge inquiries, shadow messaging, and front desk responses.",
    icon: ShieldCheck,
    color: "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/60 border-amber-200 dark:border-amber-800",
  },
  el_protocolo: {
    name: "Sofia",
    badge: "Front Desk Concierge",
    desc: "Simulate guest concierge inquiries, shadow messaging, and front desk responses.",
    icon: ShieldCheck,
    color: "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/60 border-amber-200 dark:border-amber-800",
  },
  protocolo: {
    name: "Sofia",
    badge: "Front Desk Concierge",
    desc: "Simulate guest concierge inquiries, shadow messaging, and front desk responses.",
    icon: ShieldCheck,
    color: "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/60 border-amber-200 dark:border-amber-800",
  },
  feedback: {
    name: "Feedback",
    badge: "Feedback Curation",
    desc: "Audit agent actions, evaluate rules, and curate operational knowledge.",
    icon: ShieldCheck,
    color: "text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-950/60 border-sky-200 dark:border-sky-800",
  },
};

export function NewConversationModal({
  isOpen,
  onClose,
  selectedAgent,
  onSelectAgent,
  onSubmit,
  creating,
}: NewConversationModalProps) {
  const [agents, setAgents] = useState<AgentDisplayItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    let mounted = true;
    const fetchAgents = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await usersListUsers({ is_agent: true, limit: 100 });
        const data = (res as any)?.data || res;
        const rawUsers: UserResponse[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.users)
          ? data.users
          : [];

        // Filter active agents with an agent_type
        const agentUsers = rawUsers.filter(
          (u) => u.is_agent && u.agent_type && (!u.status || u.status === "active")
        );

        const mapped: AgentDisplayItem[] = agentUsers.map((u) => {
          const rawType = (u.agent_type || "").trim();
          const type = normalizeAgentType(rawType);
          const meta = (u.payload_metadata || {}) as Record<string, any>;
          const fallback = AGENT_DEFAULTS[type] || AGENT_DEFAULTS[rawType] || {
            name: sanitizeProtocoloName(u.effective_name || u.display_name || type, "Sofia"),
            badge: "Autonomous Agent",
            desc: "Autonomous conversational AI agent for workflow automation and execution.",
            icon: Bot,
            color: "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 border-emerald-200 dark:border-emerald-800",
          };

          const rawName = u.effective_name || u.display_name || fallback.name;
          const cleanName = sanitizeProtocoloName(rawName, "Sofia");

          return {
            id: type,
            name: cleanName,
            badge: meta.badge || fallback.badge,
            desc: meta.description || fallback.desc,
            icon: fallback.icon,
            color: fallback.color,
          };
        });

        if (mounted) {
          setAgents(mapped);
          if (mapped.length > 0) {
            const hasCurrent = mapped.some((a) => a.id === selectedAgent);
            if (!hasCurrent) {
              onSelectAgent(mapped[0].id);
            }
          }
        }
      } catch (err: any) {
        if (mounted) {
          setError(err?.message || "Failed to load agent identities");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchAgents();
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/50 dark:bg-black/70 backdrop-blur-xs p-4">
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 shadow-xl max-w-lg w-full overflow-hidden">
        <div className="p-5 border-b border-stone-100 dark:border-stone-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <h3 className="text-sm font-bold text-stone-900 dark:text-stone-100">Start New Conversation</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 p-1 rounded-md transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <p className="text-xs text-stone-500 dark:text-stone-400">
            Select an autonomous agent to initiate a new bidirectional conversational session:
          </p>

          {loading ? (
            <div className="py-8 flex flex-col items-center justify-center gap-2 text-stone-400">
              <RefreshCw className="w-5 h-5 animate-spin text-emerald-600 dark:text-emerald-400" />
              <span className="text-xs font-medium">Loading agent identities...</span>
            </div>
          ) : error ? (
            <div className="p-3 rounded-xl border border-rose-200 dark:border-rose-900/50 bg-rose-50/50 dark:bg-rose-950/20 text-rose-600 dark:text-rose-400 text-xs">
              {error}
            </div>
          ) : agents.length === 0 ? (
            <div className="py-8 text-center text-xs text-stone-500 dark:text-stone-400">
              No active autonomous agents found in users registry.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2.5">
              {agents.map((agent) => {
                const Icon = agent.icon;
                const isSelected = selectedAgent === agent.id;
                return (
                  <div
                    key={agent.id}
                    onClick={() => onSelectAgent(agent.id)}
                    className={`p-3.5 rounded-xl border text-left cursor-pointer transition flex items-start gap-3 select-none ${
                      isSelected
                        ? "border-stone-900 dark:border-stone-100 bg-stone-50/70 dark:bg-stone-800/60 ring-1 ring-stone-900 dark:ring-stone-100"
                        : "border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 hover:bg-stone-50/50 dark:hover:bg-stone-800/40"
                    }`}
                  >
                    <div className={`p-2 rounded-lg border shrink-0 ${agent.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-stone-900 dark:text-stone-100">{agent.name}</span>
                        <span className="text-[10px] font-semibold px-1.5 py-0.2 rounded-full border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-600 dark:text-stone-300">
                          {agent.badge}
                        </span>
                      </div>
                      <p className="text-[11px] text-stone-500 dark:text-stone-400 mt-1 leading-snug">{agent.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-4 bg-stone-50 dark:bg-stone-950 border-t border-stone-100 dark:border-stone-800 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-xs font-medium text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={creating || loading || agents.length === 0}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white transition disabled:opacity-50"
          >
            {creating ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                <span>Creating...</span>
              </>
            ) : (
              <>
                <Plus className="w-3.5 h-3.5" />
                <span>Start Session</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
